import { Code, ConnectError } from '@connectrpc/connect';
import { useCallback, useEffect, useRef, useState } from 'react';
import { rfiSectionClient } from './rfiSectionHooks';
/** The pinned snapshot is the editor's base, never a live query that can replace dirty bytes. */ export function useRfiDocument(rfiResponseProjectId, sectionId) {
    const [snapshot, setSnapshot] = useState();
    const [remote, setRemote] = useState();
    const [versions, setVersions] = useState([]);
    const [localContent, setLocalContent] = useState();
    const [state, setState] = useState('loading');
    const [error, setError] = useState('');
    const [epoch, setEpoch] = useState(0);
    const base = useRef();
    const busy = useRef(false);
    const revision = useRef(0);
    const scope = {
        rfiResponseProjectId,
        sectionId
    };
    const accept = useCallback((next, reload)=>{
        base.current = next;
        window.dispatchEvent(new Event('rfi-document-saved'));
        setSnapshot(next);
        setRemote(undefined);
        setError('');
        setState('saved');
        if (reload) setEpoch((n)=>n + 1);
    }, []);
    const initialize = useCallback(async ()=>{
        setState('loading');
        try {
            const response = await rfiSectionClient.createRfiSectionDocument({
                rfiResponseProjectId,
                sectionId
            });
            if (!response.snapshot?.document || !response.snapshot.currentVersionId) throw new Error('Document was not returned. Retry opening the section.');
            accept(response.snapshot, false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not open the section.');
            setState('error');
        }
    }, [
        rfiResponseProjectId,
        sectionId,
        accept
    ]);
    useEffect(()=>{
        void initialize();
    }, [
        initialize
    ]);
    const readRemote = async ()=>{
        const response = await rfiSectionClient.readRfiSectionDocument({
            ...scope,
            versionId: ''
        });
        setRemote(response.snapshot);
    };
    const failed = async (err)=>{
        const connect = ConnectError.from(err);
        if (connect.code === Code.PermissionDenied || connect.code === Code.Unauthenticated) {
            base.current = undefined;
            setSnapshot(undefined);
            setRemote(undefined);
            setLocalContent(undefined);
        }
        setError(err instanceof Error ? err.message : 'The document could not be saved.');
        if (connect.code === Code.Aborted) {
            setState('conflict');
            try {
                await readRemote();
            } catch  {
            /* Preserve local bytes even if comparison is temporarily unavailable. */ }
        } else setState('error');
    };
    const checkRemote = useCallback(async ()=>{
        if (!base.current || busy.current) return;
        try {
            const { history } = await rfiSectionClient.listRfiSectionDocumentHistory({
                rfiResponseProjectId,
                sectionId
            });
            if (busy.current) return;
            setVersions(history?.versions ?? []);
            if (history?.currentVersionId && history.currentVersionId !== base.current?.currentVersionId) {
                // Includes callback writes and rejected stale callbacks: a false OO dirty event is not proof of persistence.
                setState('conflict');
                setError('The server document changed. Your editor is still open with your local work.');
                const response = await rfiSectionClient.readRfiSectionDocument({
                    rfiResponseProjectId,
                    sectionId,
                    versionId: ''
                });
                if (!busy.current) setRemote(response.snapshot);
            }
        } catch (err) {
            if ([
                Code.PermissionDenied,
                Code.Unauthenticated
            ].includes(ConnectError.from(err).code)) {
                base.current = undefined;
                setSnapshot(undefined);
                setRemote(undefined);
                setLocalContent(undefined);
                setState('error');
                setError('Section access is no longer available.');
            }
        }
    }, [
        rfiResponseProjectId,
        sectionId
    ]);
    useEffect(()=>{
        void checkRemote();
        const timer = setInterval(()=>void checkRemote(), 5000);
        const applied = ()=>void checkRemote();
        window.addEventListener('shanks-document-applied', applied);
        return ()=>{
            clearInterval(timer);
            window.removeEventListener('shanks-document-applied', applied);
        };
    }, [
        checkRemote,
        snapshot?.document?.id
    ]);
    const save = async (content, reload = false)=>{
        if (busy.current || !base.current) return false;
        busy.current = true;
        const savingRevision = revision.current;
        setLocalContent(content.slice());
        setState('saving');
        setError('');
        try {
            const response = await rfiSectionClient.saveRfiSectionDocument({
                ...scope,
                baseVersionId: base.current.currentVersionId,
                content
            });
            if (!response.snapshot) throw new Error('Save did not return a document version.');
            if (reload && revision.current !== savingRevision) {
                setRemote(response.snapshot);
                setState('conflict');
                setError('The replacement saved while you continued editing. Download your local document before opening the server version.');
            } else {
                accept(response.snapshot, reload);
                if (revision.current !== savingRevision) setState('dirty');
            }
            return true;
        } catch (err) {
            await failed(err);
            return false;
        } finally{
            busy.current = false;
        }
    };
    const restore = async (originVersionId)=>{
        if (busy.current || !base.current) return false;
        busy.current = true;
        const restoringRevision = revision.current;
        setState('saving');
        try {
            const response = await rfiSectionClient.restoreRfiSectionDocument({
                ...scope,
                baseVersionId: base.current.currentVersionId,
                originVersionId
            });
            if (!response.snapshot) throw new Error('Restore did not return a document version.');
            if (revision.current !== restoringRevision) {
                setRemote(response.snapshot);
                setState('conflict');
                setError('The restore completed while you continued editing. Your local editor is retained; download it before comparing.');
            } else {
                accept(response.snapshot, true);
                setLocalContent(undefined);
            }
            return true;
        } catch (err) {
            await failed(err);
            return false;
        } finally{
            busy.current = false;
        }
    };
    return {
        snapshot,
        remote,
        versions,
        localContent,
        state,
        error,
        epoch,
        save,
        restore,
        initialize,
        checkRemote,
        markDirty: ()=>{
            revision.current += 1;
            setState((s)=>s === 'conflict' || s === 'saving' ? s : 'dirty');
        },
        reportError: async (message)=>{
            setError(message);
            setState('error');
            await checkRemote();
        }
    };
}
